import type { components } from "../../api/schema";
import { docUrl } from "../../lib/docLinks";
import {
  Grid,
  HelpTip,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
} from "../../ui";

type AlertMessageBodies = components["schemas"]["AlertMessageBodies"];
type AlertMessageCatalog = components["schemas"]["AlertMessageCatalogResponse"];
type AlertMessageConfig = components["schemas"]["AlertMessageConfig"];
type AlertMessageSegment = components["schemas"]["AlertMessageSegment"];
type AlertMessageToken = components["schemas"]["AlertMessageToken"];
type AlertTriggerKind = components["schemas"]["AlertTriggerKind"];

const MESSAGE_STATES = [
  { key: "firing", label: "Firing" },
  { key: "recovered", label: "Recovered" },
  { key: "no_data", label: "No data" },
] as const;

function defaultBodies(): AlertMessageBodies {
  return {
    firing: [{ kind: "text", text: "Alert firing." }],
    recovered: [{ kind: "text", text: "Alert recovered." }],
    no_data: [{ kind: "text", text: "Alert has no data." }],
  };
}

function textFromSegments(segments: AlertMessageSegment[]): string {
  return segments
    .filter(
      (segment): segment is components["schemas"]["AlertMessageTextSegment"] =>
        segment.kind === "text",
    )
    .map((segment) => segment.text)
    .join("\n");
}

function tokensFromSegments(
  segments: AlertMessageSegment[],
): AlertMessageToken[] {
  return segments
    .filter(
      (segment): segment is components["schemas"]["AlertMessageTokenSegment"] =>
        segment.kind === "token",
    )
    .map((segment) => segment.token);
}

function replaceSegments(
  segments: AlertMessageSegment[],
  text: string,
  tokens: AlertMessageToken[],
): AlertMessageSegment[] {
  const next: AlertMessageSegment[] = [];
  if (text.trim().length > 0) {
    next.push({ kind: "text", text: text.trim() });
  }
  next.push(
    ...tokens.map((token): AlertMessageSegment => ({ kind: "token", token })),
  );
  return next;
}

function previewText(
  segments: AlertMessageSegment[],
  labels: Map<AlertMessageToken, string>,
): string {
  return segments
    .map((segment) =>
      segment.kind === "text"
        ? segment.text
        : `[${labels.get(segment.token) ?? segment.token}]`,
    )
    .join(" ");
}

export interface AlertRuleMessageEditorProps {
  catalog: AlertMessageCatalog;
  config: AlertMessageConfig;
  disabled?: boolean;
  onChange: (config: AlertMessageConfig) => void;
  triggerKind: AlertTriggerKind;
}

export function AlertRuleMessageEditor({
  catalog,
  config,
  disabled = false,
  onChange,
  triggerKind,
}: AlertRuleMessageEditorProps) {
  const presets = catalog.presets.filter((preset) =>
    preset.trigger_kinds.includes(triggerKind),
  );
  const tokens = catalog.tokens.filter((token) =>
    token.trigger_kinds.includes(triggerKind),
  );
  const tokenLabels = new Map(tokens.map((token) => [token.key, token.label]));
  const bodies = config.kind === "custom" ? config.bodies : defaultBodies();

  function updateBody(
    state: keyof AlertMessageBodies,
    text: string,
    selectedTokens: AlertMessageToken[],
  ) {
    onChange({
      bodies: {
        ...bodies,
        [state]: replaceSegments(bodies[state], text, selectedTokens),
      },
      kind: "custom",
    });
  }

  return (
    <Stack gap={4}>
      <Select
        disabled={disabled}
        label="Message style"
        value={config.kind}
        onChange={(event) => {
          const kind = event.currentTarget.value;
          onChange(
            kind === "preset"
              ? { kind: "preset", preset: presets[0]?.key ?? "default" }
              : { bodies, kind: "custom" },
          );
        }}
      >
        <option value="preset">Built-in preset</option>
        <option value="custom">Custom messages</option>
      </Select>

      {config.kind === "preset" ? (
        <Select
          disabled={disabled}
          label="Preset"
          value={config.preset}
          onChange={(event) => {
            onChange({
              kind: "preset",
              preset: event.currentTarget
                .value as components["schemas"]["AlertMessagePreset"],
            });
          }}
        >
          {presets.map((preset) => (
            <option key={preset.key} value={preset.key}>
              {preset.label} · {preset.description}
            </option>
          ))}
        </Select>
      ) : (
        <Stack gap={5}>
          {MESSAGE_STATES.map((state) => {
            const segments = bodies[state.key];
            const text = textFromSegments(segments);
            const selectedTokens = tokensFromSegments(segments);
            return (
              <Grid columns={2} gap={4} key={state.key}>
                <Textarea
                  disabled={disabled}
                  label={`${state.label} message`}
                  required
                  value={text}
                  onChange={(event) => {
                    updateBody(
                      state.key,
                      event.currentTarget.value,
                      selectedTokens,
                    );
                  }}
                />
                <MultiSelect
                  description="Only catalog tokens can be inserted."
                  disabled={disabled}
                  help={
                    <HelpTip
                      align="end"
                      docHref={docUrl("reference/alert-message-tokens")}
                      docLabel="Alert message tokens"
                      label="About message tokens"
                    >
                      {
                        "Each token is replaced with a value from the alert event when the notification is sent. The list is filtered to the tokens this trigger kind provides."
                      }
                    </HelpTip>
                  }
                  emptyLabel="No tokens are available for this trigger."
                  label={`${state.label} tokens`}
                  options={tokens.map((token) => ({
                    label: `${token.category} · ${token.label}`,
                    value: token.key,
                  }))}
                  value={selectedTokens}
                  onChange={(next) => {
                    updateBody(state.key, text, next as AlertMessageToken[]);
                  }}
                />
              </Grid>
            );
          })}
        </Stack>
      )}

      <Stack gap={2}>
        <Text>Notification preview</Text>
        {config.kind === "preset" ? (
          <Text variant="muted">
            Rendered at delivery time with the latest evaluation.
          </Text>
        ) : (
          MESSAGE_STATES.map((state) => (
            <Text key={state.key}>
              {state.label}: {previewText(bodies[state.key], tokenLabels)}
            </Text>
          ))
        )}
        {/* This preview is rendered client-side from the segments above.
        The server render (marker.core.alert_notifications) runs only at
        delivery time, so per-channel Slack and email previews are not
        reachable until the API exposes a render-only artifact. That absence
        is a build note, never user-facing copy. */}
        <Text variant="muted">Preview only. Nothing is sent.</Text>
      </Stack>
    </Stack>
  );
}
